import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageTransition from "../components/PageTransition";
import DateRangePicker from "../components/DateRangePicker";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import {
    FaSearch,
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaInfoCircle,
    FaCopy,
    FaTimesCircle,
    FaCheckCircle,
    FaClock,
} from "react-icons/fa";

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 5) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

const EMPTY_META_BUCKET = { count: 0, amount: 0, usdt: 0 };
const EMPTY_META = {
    pending: { ...EMPTY_META_BUCKET },
    success: { ...EMPTY_META_BUCKET },
    rejected: { ...EMPTY_META_BUCKET },
    total: { ...EMPTY_META_BUCKET },
};

function parseMetaBucket(raw) {
    return {
        count: Number(raw?.count || 0),
        amount: Number(raw?.amount || 0),
        usdt: Number(raw?.usdt || 0),
    };
}

function normalizeMeta(raw) {
    if (!raw || typeof raw !== "object") return { ...EMPTY_META };
    return {
        pending: parseMetaBucket(raw.pending),
        success: parseMetaBucket(raw.success),
        rejected: parseMetaBucket(raw.rejected),
        total: parseMetaBucket(raw.total),
    };
}

/** Both empty OK; one filled invalid; both filled must be valid YYYY-MM-DD range. */
function validateOptionalDateRange(from, to) {
    const f = String(from || "").trim();
    const t = String(to || "").trim();
    if (!f && !t) return null;
    if (!f || !t) return "from_date and to_date both are required";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f) || !/^\d{4}-\d{2}-\d{2}$/.test(t)) {
        return "Dates must be in YYYY-MM-DD format";
    }
    if (f > t) return "From Date cannot be greater than To Date";
    return null;
}

function formatDateShort(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return safeText(iso);
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return safeText(iso);
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) {}

    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied", disabled }) {
    const isDisabled = disabled || value === null || value === undefined || value === "" || value === "—";
    const onCopy = async () => {
        const ok = await copyToClipboard(String(value));
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };
    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary border-0 p-1"
            onClick={onCopy}
            disabled={isDisabled}
            title="Copy"
        >
            <FaCopy size={12} />
        </button>
    );
}

function StatusBadge({ status }) {
    const s = String(status);
    if (s === "0") {
        return <span className="fw-semibold text-warning">Pending</span>;
    }
    if (s === "1") {
        return <span className="fw-semibold text-success">Success</span>;
    }
    if (s === "2") {
        return <span className="fw-semibold text-danger">Rejected</span>;
    }
    if (s === "3") {
        return <span className="fw-semibold text-muted">Cancelled/Refunded</span>;
    }
    return <span className="fw-semibold text-muted">Unknown</span>;
}

function MetaSummaryCard({ title, bucket, tone }) {
    const tones = {
        pending: { border: "border-warning", titleClass: "text-warning", bg: "bg-warning-subtle" },
        success: { border: "border-success", titleClass: "text-success", bg: "bg-success-subtle" },
        rejected: { border: "border-danger", titleClass: "text-danger", bg: "bg-danger-subtle" },
        total: { border: "border-primary", titleClass: "text-primary", bg: "bg-primary-subtle" },
    };
    const t = tones[tone] || tones.total;
    return (
        <div className={`card h-100 border ${t.border}`}>
            <div className={`card-body py-3 ${t.bg}`}>
                <div className={`small fw-semibold text-uppercase mb-1 ${t.titleClass}`}>{title}</div>
                <div className="fw-bold fs-5 mb-1">{nfmt(bucket.count, 0)}</div>
                <div className="small text-muted">₹ {nfmt(bucket.amount)}</div>
                <div className="small text-muted">{nfmt(bucket.usdt)} USDT</div>
            </div>
        </div>
    );
}

function LoadingRow({ cols = 7 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <div className="placeholder-glow">
                        <span className="placeholder col-12" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex justify-content-between align-items-center">
            <div>
                <span className="text-muted small">
                    Page {pageNo} of {totalPages}
                </span>
            </div>

            <div className="d-flex align-items-center gap-1">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft />
                </button>

                <select
                    className="form-select form-select-sm mx-1"
                    style={{ width: "auto" }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>

                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

function StatusConfirmModal({
    show,
    type, // "success" | "reject"
    exchangeId,
    transferFrom,
    onClose,
    onConfirm,
    loading,
}) {
    const [rejectReason, setRejectReason] = useState("");

    useEffect(() => {
        if (show) {
            setRejectReason("");
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && show) onClose();
        };
        if (show) document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [show, onClose]);

    if (!show) return null;

    const title = type === "success" ? "Confirm Approve Transfer" : "Confirm Reject Transfer";
    const confirmText = type === "success" ? "Approve" : "Reject";
    const confirmColor = type === "success" ? "btn-success" : "btn-danger";
    const icon = type === "success" ? <FaCheckCircle className="me-2" /> : <FaTimesCircle className="me-2" />;

    const handleConfirm = () => {
        if (type === "reject" && !rejectReason.trim()) {
            toast.error("Reject reason is required");
            return;
        }
        onConfirm({
            exchange_id: exchangeId,
            transfer_from: transferFrom,
            status: type === "success" ? "1" : "2",
            reject_reason: type === "reject" ? rejectReason : null,
        });
    };

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1040,
                }}
                onClick={onClose}
            />

            <div
                className="modal fade show"
                style={{
                    display: "block",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1050,
                    overflowX: "hidden",
                    overflowY: "auto",
                }}
                tabIndex="-1"
                role="dialog"
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{ maxWidth: "520px", margin: "1.75rem auto" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title mb-0">{title}</h5>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                        </div>

                        <div className="modal-body">
                            {type === "reject" ? (
                                <>
                                    <div className="text-muted mb-2">
                                        Are you sure you want to reject this P2P transfer?
                                    </div>
                                    <div className="mb-2 text-danger fw-semibold">Reject reason is required</div>
                                    <label className="form-label fw-semibold">
                                        Reject Reason <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder="Enter reject reason..."
                                        required
                                        autoFocus
                                    />
                                </>
                            ) : (
                                <div className="text-muted">
                                    Are you sure you want to approve this P2P transfer?
                                </div>
                            )}
                        </div>

                        <div className="modal-footer bg-light">
                            <button
                                type="button"
                                className={`btn ${confirmColor}`}
                                onClick={handleConfirm}
                                disabled={loading || (type === "reject" && !rejectReason.trim())}
                            >
                                {icon}
                                {loading ? "Processing..." : confirmText}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function DetailsModal({ show, item, onClose, onApprove, onReject, actionLoading }) {
    useEffect(() => {
        if (show) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "auto";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && show) onClose();
        };
        if (show) document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [show, onClose]);

    if (!show || !item) return null;

    const ex = item?.exchange || {};
    const u = item?.user || {};
    const p2pUser = item?.p2p_user || {};

    const userDisplay = safeText(u?.name);
    const isP2PUserFound = p2pUser && typeof p2pUser === "object" && Object.keys(p2pUser).length > 0;
    const p2pDisplay = isP2PUserFound ? safeText(p2pUser?.name) : null;
    const canAct = String(ex?.status) === "0"; // pending

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1040,
                }}
                onClick={onClose}
            />

            <div
                className="modal fade show"
                style={{
                    display: "block",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1050,
                    overflowX: "hidden",
                    overflowY: "auto",
                }}
                tabIndex="-1"
                role="dialog"
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-centered"
                    style={{
                        maxWidth: "950px",
                        margin: "1.75rem auto",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-light">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="modal-title mb-0">P2P Transfer Details</h5>
                                <StatusBadge status={ex?.status} />
                            </div>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                        </div>

                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="card h-100 border">
                                        <div className="card-header bg-white border-bottom">
                                            <h6 className="mb-0 fw-semibold">Transfer</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row mb-3">
                                                <div className="col-12">
                                                    <div className="text-muted small mb-1">Created</div>
                                                    <div>{formatDate(ex?.create_date)}</div>
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">Amount</div>
                                                    <div className="fw-bold fs-5">{nfmt(ex?.amount)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">USDT</div>
                                                    <div className="fw-bold fs-5">{nfmt(ex?.usdt)}</div>
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">UTR</div>
                                                    <div className="fw-semibold">{safeText(ex?.utr)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">UPI ID</div>
                                                    <div className="fw-semibold">{safeText(ex?.upi_id)}</div>
                                                </div>
                                            </div>

                                            <div className="mb-0">
                                                <div className="text-muted small mb-1">Transfer File</div>
                                                {ex?.file ? (
                                                    <div className="bg-light border rounded p-2">
                                                        <img
                                                            src={ex.file}
                                                            alt="P2P transfer file"
                                                            style={{
                                                                width: "100%",
                                                                maxHeight: 260,
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="text-muted">—</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="card h-100 border">
                                        <div className="card-header bg-white border-bottom">
                                            <h6 className="mb-0 fw-semibold">Users</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <div className="text-muted small mb-1">Request User</div>
                                                <div className="fw-semibold">{userDisplay}</div>
                                                <div className="text-muted small">
                                                    User ID: <span className="fw-semibold">{safeText(u?.user_id)}</span>
                                                </div>
                                                <div className="text-muted small">
                                                    Mobile: {safeText(u?.mobile)} ({safeText(u?.country_code)})
                                                </div>
                                                <div className="text-muted small">Email: {safeText(u?.email)}</div>
                                            </div>

                                            <div className="mb-3">
                                                <div className="text-muted small mb-1">P2P User</div>
                                                {isP2PUserFound ? (
                                                    <>
                                                        <div className="fw-semibold">{p2pDisplay}</div>
                                                        <div className="text-muted small">
                                                            User ID: <span className="fw-semibold">{safeText(p2pUser?.user_id)}</span>
                                                        </div>
                                                        <div className="text-muted small">
                                                            Mobile: {safeText(p2pUser?.mobile)} ({safeText(p2pUser?.country_code)})
                                                        </div>
                                                        <div className="text-muted small">Email: {safeText(p2pUser?.email)}</div>
                                                    </>
                                                ) : (
                                                    <div className="text-danger fw-semibold">User not found</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer bg-light">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                            {canAct && (
                                <div className="d-flex gap-2 flex-wrap justify-content-end ms-auto">
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={() => onApprove?.(ex?.exchange_id)}
                                        disabled={actionLoading}
                                        title="Approve (success)"
                                    >
                                        <FaCheckCircle className="me-2" />
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => onReject?.(ex?.exchange_id)}
                                        disabled={actionLoading}
                                        title="Reject (rejected)"
                                    >
                                        <FaTimesCircle className="me-2" />
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default function P2PTransferList() {
    const [loading, setLoading] = useState(true);
    const [inputText, setInputText] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [tab, setTab] = useState("all");
    const [pageNo, setPageNo] = useState(1);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({ total_exchange: 0, page_no: 1 });
    const [meta, setMeta] = useState(EMPTY_META);
    const [selectedItem, setSelectedItem] = useState(null);
    const lastDateErrorRef = useRef("");
    // Requested: randomly pick transfer_from from this pool for approve/reject
    const TRANSFER_FROM_POOL = [
        "3v38fc7i7yyfb507aqrm8f66qge3m0h2gwy4851maqn",
        "fyc2b1875je6187bu3v4hfnbpy757g4x7h14w62t4r0",
        "eyt3a10h7799r01810tov5uwnjs7632bc7we673lw50",
    ];
    const [statusConfirm, setStatusConfirm] = useState({
        show: false,
        type: null, // "success" | "reject"
        exchange_id: null,
        transfer_from: null,
    });
    const [statusLoading, setStatusLoading] = useState(false);

    const PAGE_SIZE = 20; // Backend limit = 20

    const totalPages = useMemo(() => {
        const total = Number(info?.total_exchange || 0);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / PAGE_SIZE));
    }, [info?.total_exchange]);

    const statusParam = useMemo(() => {
        if (tab === "pending") return "0";
        if (tab === "success") return "1";
        if (tab === "rejected") return "2";
        return "";
    }, [tab]);

    const fetchTransfers = useCallback(async ({ page, query, status, from_date, to_date }) => {
        const dateErr = validateOptionalDateRange(from_date, to_date);
        if (dateErr) {
            if (lastDateErrorRef.current !== dateErr) {
                lastDateErrorRef.current = dateErr;
                toast.error(dateErr);
            }
            setList([]);
            setInfo({ total_exchange: 0, page_no: page });
            setMeta(EMPTY_META);
            setLoading(false);
            return;
        }
        lastDateErrorRef.current = "";

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/p2p-transfer-list`,
                headers: { "Content-Type": "application/json" },
                data: {
                    page_no: page,
                    query: query || "",
                    status: status || "",
                    from_date: from_date || "",
                    to_date: to_date || "",
                    token: localStorage.getItem("token") || "",
                },
            });

            const data = res?.data;
            if (data?.error) {
                toast.error(typeof data?.error === "string" ? data.error : "Failed to load P2P transfers");
                setList([]);
                setInfo({ total_exchange: 0, page_no: page });
                setMeta(EMPTY_META);
                return;
            }

            const raw = Array.isArray(data?.exchange_list) ? data.exchange_list : [];
            setList(raw);

            const apiInfo = data?.info || {};
            setInfo({
                total_exchange: Number(apiInfo?.total_exchange || 0),
                page_no: Number(apiInfo?.page_no || page),
            });
            setMeta(normalizeMeta(data?.meta));
        } catch (err) {
            console.log(err);
            toast.error("Failed to load P2P transfers");
            setList([]);
            setInfo({ total_exchange: 0, page_no: page });
            setMeta(EMPTY_META);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateTransferStatus = useCallback(
        async ({ exchange_id, transfer_from, status, reject_reason }) => {
            if (!exchange_id || !transfer_from) {
                toast.error("Missing exchange information");
                return;
            }

            setStatusLoading(true);
            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/p2p-transfer-status`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token") || "",
                        exchange_id,
                        transfer_from,
                        status,
                        reject_reason,
                    },
                });

                const out = res?.data;
                if (out?.error) {
                    toast.error(out?.error === "session expired" ? "Session expired. Please login again." : (out?.error || "Failed to update"));
                    return;
                }

                toast.success(out?.msg || "P2P transfer status updated successfully");

                // Close modals and refresh current tab/page
                setStatusConfirm({ show: false, type: null, exchange_id: null, transfer_from: null });
                setSelectedItem(null);
                await fetchTransfers({
                    page: pageNo,
                    query: appliedQuery,
                    status: statusParam,
                    from_date: fromDate,
                    to_date: toDate,
                });
            } catch (err) {
                console.error(err);
                toast.error("Failed to update P2P transfer status");
            } finally {
                setStatusLoading(false);
            }
        },
        [appliedQuery, fetchTransfers, fromDate, pageNo, statusParam, toDate]
    );

    const requestApprove = (exchange_id) => {
        const randomFrom =
            TRANSFER_FROM_POOL[Math.floor(Math.random() * TRANSFER_FROM_POOL.length)];
        setStatusConfirm({
            show: true,
            type: "success",
            exchange_id,
            transfer_from: randomFrom,
        });
    };

    const requestReject = (exchange_id) => {
        const randomFrom =
            TRANSFER_FROM_POOL[Math.floor(Math.random() * TRANSFER_FROM_POOL.length)];
        setStatusConfirm({
            show: true,
            type: "reject",
            exchange_id,
            transfer_from: randomFrom,
        });
    };

    useEffect(() => {
        fetchTransfers({
            page: pageNo,
            query: appliedQuery,
            status: statusParam,
            from_date: fromDate,
            to_date: toDate,
        });
    }, [pageNo, appliedQuery, statusParam, fromDate, toDate, fetchTransfers]);

    const onSearch = () => {
        setPageNo(1);
        setAppliedQuery(inputText.trim());
    };

    const onDateRangeChange = ({ from_date, to_date }) => {
        setFromDate(from_date || "");
        setToDate(to_date || "");
        setPageNo(1);
    };

    const onRefreshReset = async () => {
        setInputText("");
        setAppliedQuery("");
        setTab("all");
        setFromDate("");
        setToDate("");
        setPageNo(1);
        await fetchTransfers({
            page: 1,
            query: "",
            status: "",
            from_date: "",
            to_date: "",
        });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    const openDetails = (item) => setSelectedItem(item);
    const closeDetails = () => setSelectedItem(null);

    return (
        <PageTransition>
            <div className="container-fluid py-3">
                <div className="row mb-3">
                    <div className="col">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h1 className="h3 mb-1">P2P Transfer List</h1>
                                <p className="text-muted mb-0">
                                    Total: <strong>{nfmt(info.total_exchange, 0)}</strong>
                                    {appliedQuery && (
                                        <span className="ms-2">
                                            • Search: <strong>{appliedQuery}</strong>
                                        </span>
                                    )}
                                    {(fromDate || toDate) && (
                                        <span className="ms-2">
                                            • Date:{" "}
                                            <strong>
                                                {fromDate || "—"} → {toDate || "—"}
                                            </strong>
                                        </span>
                                    )}
                                </p>
                            </div>
                            <button className="btn btn-outline-primary" onClick={onRefreshReset} disabled={loading}>
                                <FaSyncAlt className={`me-2 ${loading ? "spin" : ""}`} />
                                {loading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Pending" bucket={meta.pending} tone="pending" />
                    </div>
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Success" bucket={meta.success} tone="success" />
                    </div>
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Rejected" bucket={meta.rejected} tone="rejected" />
                    </div>
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Total" bucket={meta.total} tone="total" />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                    <ul className="nav nav-tabs border-0">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "all" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("all");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaSearch className="me-2" />
                                                All
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "pending" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("pending");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaClock className="me-2" />
                                                Pending
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "success" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("success");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaCheckCircle className="me-2" />
                                                Success
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "rejected" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("rejected");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaTimesCircle className="me-2" />
                                                Rejected
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="d-flex align-items-center gap-2">
                                        <div className="input-group input-group-sm" style={{ width: "300px" }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search by user name/mobile/email/id..."
                                                value={inputText}
                                                onChange={(e) => setInputText(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                                            />
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={onSearch}
                                                disabled={loading}
                                            >
                                                <FaSearch />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-2 align-items-end">
                                    <div className="col-12 col-md-5 col-lg-4">
                                        <DateRangePicker
                                            fromDate={fromDate}
                                            toDate={toDate}
                                            onChange={onDateRangeChange}
                                            disabled={loading}
                                            label="Date range"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <div className="card">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "5%" }}>#</th>
                                                <th style={{ width: "18%" }}>Date</th>
                                                <th style={{ width: "18%" }}>User</th>
                                                <th style={{ width: "12%" }}>Amount</th>
                                                <th style={{ width: "12%" }}>UTR</th>
                                                <th style={{ width: "18%" }}>UPI ID</th>
                                                <th style={{ width: "12%" }}>USDT</th>
                                                <th style={{ width: "12%" }}>Status</th>
                                                <th style={{ width: "6%" }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[...Array(5)].map((_, i) => (
                                                        <LoadingRow key={i} cols={9} />
                                                    ))}
                                                </>
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <FaSearch className="mb-2" size={24} />
                                                            <p className="mb-0">No transfers found</p>
                                                            {appliedQuery && <small>Try changing your search query</small>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((row, index) => {
                                                    const ex = row?.exchange || {};
                                                    const p2pUser = row?.p2p_user || {};
                                                    const isP2PUserFound = p2pUser && typeof p2pUser === "object" && Object.keys(p2pUser).length > 0;
                                                    const p2pDisplay = isP2PUserFound ? safeText(p2pUser?.name) : null;
                                                    const serialNo = (pageNo - 1) * PAGE_SIZE + index + 1;

                                                    return (
                                                        <tr key={ex?.exchange_id}>
                                                            <td className="text-muted">{serialNo}</td>
                                                            <td>
                                                                <div className="fw-medium">{formatDate(ex?.create_date)}</div>
                                                            </td>
                                                            <td>
                                                                {isP2PUserFound ? (
                                                                    <div className="fw-semibold">{p2pDisplay}</div>
                                                                ) : (
                                                                    <div className="text-danger fw-semibold">User not found</div>
                                                                )}
                                                            </td>
                                                            <td className="fw-bold">{nfmt(ex?.amount)}</td>
                                                            <td>{safeText(ex?.utr)}</td>
                                                            <td>{safeText(ex?.upi_id)}</td>
                                                            <td>{nfmt(ex?.usdt)}</td>
                                                            <td>
                                                                <StatusBadge status={ex?.status} />
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    onClick={() => openDetails(row)}
                                                                    title="View Details"
                                                                >
                                                                    <FaInfoCircle />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && list.length > 0 && (
                                    <div className="card-footer">
                                        <PaginationBar
                                            loading={loading}
                                            pageNo={pageNo}
                                            totalPages={totalPages}
                                            onPrev={onPrev}
                                            onNext={onNext}
                                            onJump={onJump}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <DetailsModal
                    show={!!selectedItem}
                    item={selectedItem}
                    onClose={closeDetails}
                    onApprove={requestApprove}
                    onReject={requestReject}
                    actionLoading={statusLoading}
                />

                <StatusConfirmModal
                    show={statusConfirm.show}
                    type={statusConfirm.type}
                    exchangeId={statusConfirm.exchange_id}
                    transferFrom={statusConfirm.transfer_from}
                    onClose={() => setStatusConfirm({ show: false, type: null, exchange_id: null, transfer_from: null })}
                    loading={statusLoading}
                    onConfirm={updateTransferStatus}
                />

                <style jsx>{`
                    .nav-tabs .nav-link {
                        border: none;
                        color: #6c757d;
                        padding: 0.5rem 1rem;
                    }
                    .nav-tabs .nav-link.active {
                        color: #0d6efd;
                        border-bottom: 2px solid #0d6efd;
                        background: transparent;
                    }
                    .nav-tabs .nav-link:hover:not(.active) {
                        color: #495057;
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                    .table th {
                        font-weight: 600;
                        font-size: 0.875rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .table td {
                        vertical-align: middle;
                        font-size: 0.9rem;
                    }
                `}</style>
            </div>
        </PageTransition>
    );
}

